import { DestroyRef, inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { catchError, forkJoin, Observable, of } from "rxjs";
import {
  Constants,
  OfflineModeActivator,
  OfflineModeService,
  OfflineSelection
} from "@frontends-v3/shared-core";
import { NzMessageService } from "ng-zorro-antd/message";
import { NzModalService } from "ng-zorro-antd/modal";

import { ContentAssignmentService } from "../../core/services/content-assignment.service";
import { LibraryService } from "../../core/services/library.service";

import { OfflineAssignmentPickerModalComponent } from "./ui-offline-assignment-picker/offline-assignment-picker-modal.component";

/**
 * DEV-966 — implementação do `OFFLINE_MODE_ACTIVATOR` no platform: abre a janela de escolha,
 * liga o modo no servidor e pré-carrega o snapshot da atribuição escolhida (lista, detalhe do
 * conteúdo, participação e status das aulas). O `offlineHttpInterceptor` grava tudo o que passa
 * por ele — o prefetch é só disparar as leituras.
 */
@Injectable({ providedIn: "root" })
export class OfflineModeActivatorService implements OfflineModeActivator {
  private readonly modalService = inject(NzModalService);
  private readonly message = inject(NzMessageService);
  private readonly offlineMode = inject(OfflineModeService);
  private readonly assignmentService = inject(ContentAssignmentService);
  private readonly libraryService = inject(LibraryService);
  private readonly destroyRef = inject(DestroyRef);

  // Janela de escolha já aberta: um segundo acionamento do toggle não empilha outra modal.
  private pickerOpen = false;

  public requestEnable(): void {
    if (this.pickerOpen) return;
    this.pickerOpen = true;

    const modalRef = this.modalService.create<
      OfflineAssignmentPickerModalComponent,
      undefined,
      OfflineSelection | null
    >({
      nzContent: OfflineAssignmentPickerModalComponent,
      nzFooter: null,
      nzClosable: false,
      nzCentered: true,
      nzWidth: Constants.MODAL_MEDIUM_SIZE,
      nzWrapClassName: "offline-picker-modal-wrap"
    });

    modalRef.afterClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selection: OfflineSelection | null | undefined) => {
        this.pickerOpen = false;
        if (!selection) return;
        this.enable(selection);
      });
  }

  private enable(selection: OfflineSelection): void {
    const loading = this.message.loading("Preparando o Modo Offline…", { nzDuration: 0 });

    this.offlineMode
      .enable(selection, () => this.prefetch(selection))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.message.remove(loading.messageId);
          this.message.success(
            "Modo Offline ligado. Sua atribuição está pronta para a sala de aula."
          );
        },
        // DEV-1050: o backend aceita toda atribuição da lista (inclusive a que os alunos concluem),
        // então qualquer falha aqui é de rede ou servidor.
        error: () => {
          this.message.remove(loading.messageId);
          this.message.error(
            "Não foi possível ligar o Modo Offline. Verifique a conexão e tente de novo."
          );
        }
      });
  }

  private prefetch(selection: OfflineSelection): Observable<unknown> {
    const safe = <T>(source: Observable<T>): Observable<T | null> =>
      source.pipe(catchError(() => of(null)));

    return forkJoin({
      assignments: safe(this.assignmentService.getProfessorAssignments()),
      participation: safe(this.assignmentService.getParticipation(selection.idAssignment)),
      lessonStatus: safe(this.assignmentService.getLessonStatus(selection.idAssignment)),
      content: safe(this.libraryService.getContentEssentials(selection.idContentTemplate))
    });
  }
}
